<template>
  <div class="instrument-card">
    <div v-if="isLoading" class="state-message">Loading card…</div>

    <form v-else-if="isEditing" class="card-form" @submit.prevent="save">
      <fieldset v-for="section in sections" :key="section.title" class="card-section">
        <legend>{{ section.title }}</legend>
        <div v-for="field in section.fields" :key="field.key" class="card-field">
          <label :for="`card-${field.key}`">{{ field.label }}</label>
          <textarea
            :id="`card-${field.key}`"
            ref="textareas"
            v-model="form[field.key]"
            rows="1"
            :class="{ 'is-highlighted': field.key === highlightedField }"
            @input="autoResize($event.target)"
          ></textarea>
        </div>
      </fieldset>
      <div class="buttons-container">
        <button type="submit" :disabled="isSaving">{{ card ? 'Save' : 'Create card' }}</button>
        <button type="button" :disabled="isSaving" @click="isEditing = false">Cancel</button>
      </div>
    </form>

    <div v-else-if="!card" class="empty-state">
      <i class="bi bi-card-text" aria-hidden="true"></i>
      <p class="empty-title">This instrument has no card yet.</p>
      <p class="empty-text">
        A card holds maintenance and safety information that can be printed and placed on the instrument.
      </p>
      <button type="button" @click="startEdit">Create card</button>
    </div>

    <div v-else>
      <div class="meta-strip">
        <span><strong>ID:</strong> {{ card.serial || 'N/A' }}</span>
        <span><strong>Location:</strong> {{ card.location_name || 'N/A' }}</span>
        <span><strong>Responsible:</strong> {{ card.user_name || 'N/A' }}</span>
      </div>

      <div v-if="card[highlightedField]" class="shutdown-box">
        <div class="shutdown-title">
          <i class="bi bi-exclamation-octagon-fill" aria-hidden="true"></i> Emergency shutdown
        </div>
        <p>{{ card[highlightedField] }}</p>
      </div>

      <section v-for="section in viewSections" :key="section.title" class="card-section">
        <h4>{{ section.title }}</h4>
        <dl>
          <template v-for="field in section.fields" :key="field.key">
            <dt>{{ field.label }}</dt>
            <dd :class="{ 'is-empty': !card[field.key] }">{{ card[field.key] || 'Not specified' }}</dd>
          </template>
        </dl>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import apiService from '@/services/apiService';
import { API_ENDPOINTS } from '@/services/config';
import { confirmDelete } from '@/utils/confirmDelete';
import { CARD_FIELDS, CARD_SECTIONS, HIGHLIGHTED_FIELD } from '@/utils/instrumentCard';

const emptyForm = () => Object.fromEntries(CARD_FIELDS.map((key) => [key, '']));

export default {
  name: 'InstrumentCard',
  props: {
    instrumentId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['hasCardChange'],
  setup() {
    return {
      toast: useToast(),
      sections: CARD_SECTIONS,
      highlightedField: HIGHLIGHTED_FIELD,
    };
  },
  data() {
    return {
      card: null,
      form: emptyForm(),
      isLoading: false,
      isEditing: false,
      isSaving: false,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    viewSections() {
      return CARD_SECTIONS
        .map((section) => ({
          ...section,
          fields: section.fields.filter((field) => field.key !== HIGHLIGHTED_FIELD),
        }))
        .filter((section) => section.fields.length);
    },
  },
  watch: {
    card(value) {
      this.$emit('hasCardChange', Boolean(value));
    },
  },
  created() {
    this.fetchCard();
  },
  methods: {
    async fetchCard() {
      this.isLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.instrumentCard(this.instrumentId));
        this.card = response.data;
      } catch (error) {
        this.card = null;
        if (error?.response?.status !== 404) {
          this.toast.error('Failed to fetch instrument card');
          console.error('Failed to fetch instrument card:', error);
        }
      } finally {
        this.isLoading = false;
      }
    },
    startEdit() {
      this.form = Object.fromEntries(CARD_FIELDS.map((key) => [key, this.card?.[key] || '']));
      this.isEditing = true;
      this.$nextTick(() => (this.$refs.textareas || []).forEach(this.autoResize));
    },
    autoResize(textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight + 2}px`;
    },
    async save() {
      const payload = Object.fromEntries(
        CARD_FIELDS.map((key) => [key, this.form[key]?.trim() ? this.form[key].trim() : null])
      );

      this.isSaving = true;
      try {
        const response = await axios.put(API_ENDPOINTS.instrumentCard(this.instrumentId), payload, {
          headers: { 'Content-Type': 'application/json' },
        });
        this.toast.success(response.status === 201 ? 'Instrument card created' : 'Instrument card saved');
        this.isEditing = false;
        await this.fetchCard();
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to save instrument card');
        console.error('Failed to save instrument card:', error);
      } finally {
        this.isSaving = false;
      }
    },
    async deleteCard() {
      if (!this.card) return;
      const isConfirmed = await confirmDelete('Delete this instrument card? The instrument itself is kept.');
      if (!isConfirmed) return;

      try {
        await apiService.deleteResource(
          API_ENDPOINTS.instrumentCard(this.instrumentId),
          this.loggedInUser?.id,
          this.loggedInUser?.entra_id
        );
        this.card = null;
        this.isEditing = false;
        this.toast.success('Instrument card deleted');
      } catch (error) {
        this.toast.error(error?.response?.data?.message || 'Failed to delete instrument card');
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.instrument-card {
  .state-message {
    padding: 16px 0;
    color: rgba($primary-color, 0.7);
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 24px 12px;
    text-align: center;

    .bi {
      font-size: 2rem;
      color: rgba($primary-color, 0.5);
    }

    .empty-title {
      margin: 0;
      font-weight: 600;
      color: $primary-color;
    }

    .empty-text {
      max-width: 420px;
      margin: 0 0 8px;
      color: rgba(0, 0, 0, 0.65);
    }

    button {
      width: auto;
    }
  }

  .meta-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    padding: 8px 12px;
    margin-bottom: 12px;
    border-radius: 6px;
    background: rgba($primary-color, 0.06);
    font-size: 0.95rem;
  }

  .shutdown-box {
    padding: 10px 14px;
    margin-bottom: 14px;
    border: 2px solid #b02020;
    border-radius: 6px;
    background: #fdecec;

    .shutdown-title {
      font-weight: 700;
      color: #b02020;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    p {
      margin: 4px 0 0;
      white-space: pre-wrap;
    }
  }

  .card-section {
    margin-bottom: 10px;

    h4,
    legend {
      margin-bottom: 4px;
      padding-bottom: 2px;
      border-bottom: 1px solid rgba($primary-color, 0.2);
      color: $primary-color;
      font-size: 1.05rem;
      font-weight: 600;
    }

    dl {
      margin: 0;
    }

    dt {
      font-weight: 600;
    }

    dd {
      margin: 0 0 4px;
      white-space: pre-wrap;

      &.is-empty {
        color: rgba(0, 0, 0, 0.45);
        font-style: italic;
      }
    }
  }

  fieldset.card-section {
    border: 0;
    padding: 0;

    legend {
      width: 100%;
    }
  }

  textarea {
    display: block;
    width: 100%;
    margin: 0;
    padding: 4px 8px;
    line-height: 1.4;
    resize: none;
    overflow: hidden;

    &.is-highlighted {
      border-color: #b02020;
    }
  }

  .card-field {
    margin-bottom: 6px;

    label {
      display: block;
      margin: 0 0 2px;
      font-size: 0.85rem;
      font-weight: 600;
    }
  }

  .card-form .card-section {
    margin-bottom: 8px;
  }

  .buttons-container {
    display: flex;
    justify-content: space-between;
  }
}
</style>
